import type { SearchHistory } from '../types/search'

const STORAGE_KEY = 'bible-search-history'
const MAX_ITEMS_PER_VERSION = 20
const MAX_VERSIONS = 5

interface StorageData {
  searches: SearchHistory[]
  maxItems: number
}

class SearchHistoryManager {
  /**
   * 生成唯一識別符（使用加密安全隨機數生成器）
   */
  private generateId(): string {
    const randomBytes = crypto.getRandomValues(new Uint8Array(6))
    const randomPart = Array.from(randomBytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
    return `${Date.now()}-${randomPart}`
  }

  /**
   * 讀取 LocalStorage
   */
  private getStorageData(): StorageData {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      return data ? JSON.parse(data) : { searches: [], maxItems: MAX_ITEMS_PER_VERSION }
    } catch {
      return { searches: [], maxItems: MAX_ITEMS_PER_VERSION }
    }
  }

  /**
   * 寫入 LocalStorage
   */
  private setStorageData(data: StorageData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save search history:', error)
    }
  }

  /**
   * 保存搜尋紀錄
   * - 檢查重複（相同 query + versionCode）
   * - 若重複，刪除舊紀錄並插入新紀錄到頂部
   * - 若新增，直接插入到頂部
   * - 保留最近 20 筆（超過則刪除最舊）
   */
  save(search: Omit<SearchHistory, 'id'>): void {
    const data = this.getStorageData()
    const { query, versionCode, resultCount, timestamp, isRegex } = search

    // 移除相同 query 和 versionCode 的舊紀錄
    data.searches = data.searches.filter(
      s => !(s.query === query && s.versionCode === versionCode)
    )

    // 在頂部插入新紀錄
    const newHistory: SearchHistory = {
      id: this.generateId(),
      query,
      versionCode,
      resultCount,
      timestamp,
      isRegex,
    }
    data.searches.unshift(newHistory)

    // 按版本分組統計
    const versionedSearches = new Map<string, SearchHistory[]>()

    for (const s of data.searches) {
      if (!versionedSearches.has(s.versionCode)) {
        versionedSearches.set(s.versionCode, [])
      }
      versionedSearches.get(s.versionCode)!.push(s)
    }

    // 每個版本最多保留 20 筆
    const filtered: SearchHistory[] = []
    for (const [, searches] of versionedSearches) {
      filtered.push(...searches.slice(0, MAX_ITEMS_PER_VERSION))
    }

    // 整體最多保留 100 筆（5 個版本 × 20 筆）
    data.searches = filtered.slice(0, MAX_VERSIONS * MAX_ITEMS_PER_VERSION)

    this.setStorageData(data)
  }

  /**
   * 取得搜尋歷史
   * - 過濾指定版本的歷史
   * - 按時間戳排序（最新在前）
   * - 返回最多 20 筆
   */
  getHistory(versionCode: string): SearchHistory[] {
    const data = this.getStorageData()
    return data.searches
      .filter(s => s.versionCode === versionCode)
      .slice(0, MAX_ITEMS_PER_VERSION)
  }

  /**
   * 清除指定版本的歷史
   */
  clearHistory(versionCode: string): void {
    const data = this.getStorageData()
    data.searches = data.searches.filter(s => s.versionCode !== versionCode)
    this.setStorageData(data)
  }

  /**
   * 清除所有歷史
   */
  clearAll(): void {
    this.setStorageData({ searches: [], maxItems: MAX_ITEMS_PER_VERSION })
  }

  /**
   * 取得所有歷史
   */
  getAllHistory(): SearchHistory[] {
    const data = this.getStorageData()
    return data.searches
  }
}

export const searchHistoryManager = new SearchHistoryManager()
